import { useMachine } from "@xstate/react/lib";
import animateScrollTo from "animated-scroll-to";
import { useMediaQuery } from "beautiful-react-hooks";
import indexBy from "ramda/src/indexBy";
import prop from "ramda/src/prop";
import * as React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { mobileBreakpoint } from "../design_constants";
import { Breadcrumbs } from "./components/breadcrumbs";
import { CategoryHeadline } from "./components/category_headline";
import { CategorySidebar } from "./components/category_sidebar";
import { LoadingPlaceholder } from "./components/loading_placeholder";
import { ProductList } from "./components/catalog_item_list";
import { RetryButton } from "./components/retry_button";
import { commonSortingOptions, defaultSorting } from "./constants";
import { AppliedFilterControls } from "./filter/applied_filter/applied_filter_controls";
import { AvailableFilterOptions } from "./filter/applied_filter/filter";
import { categoryFilters } from "./filter/available_filters";
import { FilterSidebar } from "./filter/filter_sidebar";
import { useCategoryDetail } from "./helpers/use_category_detail";
import { AlterFilterEvent, CatalogMachine } from "./machines/catalog_machine";
import styles from "./components/error_page.module.sass";
import { ErrorPage } from "./components/error_page";
import { isIE } from "react-device-detect";
import { UnsupportedBrowser } from "./components/unsupported_browser";
import { Bestsellers } from "./components/bestsellers";
import { fetchInitial } from "./api/product_catalog_api";

const productsPerPage = 3 * 6;

type ProductCatalogCategoryProps = {
  categoryId: number;
  baseUrl: string;
  appliedSeoUrlFilters: { brandId: Array<number> };
  categorySeoAttributes: { description: string; name: string };
  membersOnlyProducts: Record<number, number>;
};

type ToggleMobileFiltersShownButtonProps = {
  children: React.ReactNode;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className: string;
};

const ToggleMobileFiltersShownButton = ({
  children,
  onClick,
  className,
}: ToggleMobileFiltersShownButtonProps) => (
  <button onClick={onClick} className={className}>
    {children}
  </button>
);

export const ProductCatalogCategory = ({
  baseUrl,
  categoryId,
  appliedSeoUrlFilters,
  categorySeoAttributes,
  membersOnlyProducts,
}: ProductCatalogCategoryProps): JSX.Element => {
  const productsListRef = useRef<HTMLUListElement>(null);

  const sortParam = location.search.split("sort=")[1];

  const selectDefaultSorting = () => {
    switch (sortParam) {
      case "averageRating":
        send({
          type: "ALTER_FILTER",
          newFilter: {
            ...state.context.filter,
            sort: commonSortingOptions[1].requestSorting,
          },
        } as AlterFilterEvent);
        return commonSortingOptions[1].requestSorting;
      case "asc_price":
        send({
          type: "ALTER_FILTER",
          newFilter: {
            ...state.context.filter,
            sort: commonSortingOptions[2].requestSorting,
          },
        } as AlterFilterEvent);
        return commonSortingOptions[2].requestSorting;
      case "desc_price":
        send({
          type: "ALTER_FILTER",
          newFilter: {
            ...state.context.filter,
            sort: commonSortingOptions[3].requestSorting,
          },
        } as AlterFilterEvent);
        return commonSortingOptions[3].requestSorting;
      default:
        send({
          type: "ALTER_FILTER",
          newFilter: {
            ...state.context.filter,
            sort: commonSortingOptions[0].requestSorting,
          },
        } as AlterFilterEvent);
        return commonSortingOptions[0].requestSorting;
    }
  };

  const [state, send] = useMachine(CatalogMachine, {
    context: {
      filter: { categoryId },
      baseUrl,
      appliedSeoUrlFilters,
      pagination: {
        currentPage: 1,
        perPage: productsPerPage,
        resultsCount: 0,
      },
    },
    actions: {
      scrollToTopOfProductsList: async (_context, _event) => {
        if (productsListRef.current !== null) {
          await animateScrollTo(productsListRef.current, {
            verticalOffset: -50,
            speed: 400,
          });
        }
      },
    },
  });

  const {
    categoryIsLoading, // TODO: Render a separate loader/placeholder for category
    breadcrumbs,
    category,
    retryCategoryLoad,
  } = useCategoryDetail(baseUrl, categoryId);

  const isMobile = useMediaQuery(`(max-width: ${mobileBreakpoint}px)`);
  const [mobileFiltersShown, setMobileFiltersShown] = useState(false);

  const onFilterChange = useCallback(
    (newFilter) => {
      send({ type: "ALTER_FILTER", newFilter });
    },
    [send]
  );

  const onFilterReset = useCallback(() => {
    send({ type: "RESET_FILTER" });
  }, [send]);

  const onFilterPropertyRemove = useCallback(
    (newFilter) => {
      const event: AlterFilterEvent = { newFilter, type: "ALTER_FILTER" };
      send(event);
    },
    [state.context.filter, send]
  );

  const availableFilterOptions: AvailableFilterOptions = useMemo(
    () => ({
      allTextParameters: state.context.allTextParameterValues,
      brandsById: indexBy(
        prop("id"),
        (state.context.filterOptions?.brands ?? []).map((brand, index) => ({
          ...brand,
          index,
        }))
      ),
      parametersById: indexBy(
        prop("id"),
        Object.values(
          state.context.filterOptions?.parameters ?? []
        ).map((parameter, index) => ({ ...parameter, index }))
      ),
    }),
    [
      state.context.filterOptions?.parameters,
      state.context.filterOptions?.brands,
      state.context.allTextParameterValues,
    ]
  );

  if (isIE) return <UnsupportedBrowser />;

  if (state.matches("loadingInitialView")) {
    return <LoadingPlaceholder />;
  }

  if (
    state.matches("initializationFailed") ||
    state.matches("productsDisplayed.fetchingFailed")
  ) {
    return (
      <div className={"error-page " + styles.categoryErrorPage}>
        <ErrorPage />
        <RetryButton
          onRetry={() => {
            send("RETRY");
            retryCategoryLoad?.();
          }}
        />
        {/*{process.env.NODE_ENV === "development" && (*/}
        {/*  <pre>{JSON.stringify(state.context.error?.details, null, 2)}</pre>*/}
        {/*)}*/}
      </div>
    );
  }

  const filterSidebar = (
    <FilterSidebar
      filterOptions={state.context.filterOptions}
      appliedSeoUrlFilters={appliedSeoUrlFilters}
      filterableCounts={state.context.filterableCounts}
      allTextParameterValues={state.context.allTextParameterValues}
      onChange={onFilterChange}
      onReset={onFilterReset}
      filter={state.context.filter}
    />
  );

  return (
    <div>
      <div id="page-wrapper">
        <div id="page">
          {breadcrumbs !== null && (
            <Breadcrumbs navigationItems={breadcrumbs} />
          )}
          {isMobile && (
            <div className="mobile-product-filter">
              {!mobileFiltersShown ? (
                <ToggleMobileFiltersShownButton
                  onClick={() => setMobileFiltersShown(true)}
                  className={"filters-button collapsed"}
                >
                  Upřesnit parametry
                </ToggleMobileFiltersShownButton>
              ) : (
                <>
                  <ToggleMobileFiltersShownButton
                    onClick={() => setMobileFiltersShown(false)}
                    className={"filters-button"}
                  >
                    Upřesnit parametry
                  </ToggleMobileFiltersShownButton>
                  <div id="mobile-product-filters">{filterSidebar}</div>
                </>
              )}
            </div>
          )}

          {category !== null && (
            <CategoryHeadline
              category={category}
              bestsellers={state.context.bestsellers}
              appliedSeoUrlFilters={appliedSeoUrlFilters}
              categorySeoAttributes={categorySeoAttributes}
              pagination={state.context.pagination}
            />
          )}
          <AppliedFilterControls
            appliedFilter={state.context.filter}
            availableFilters={categoryFilters}
            filterOptions={availableFilterOptions}
            onRemove={onFilterPropertyRemove}
          />
          {state.context.products.length > 0 && (
            <ProductList
              ref={productsListRef}
              products={state.context.products}
              membersOnlyProducts={membersOnlyProducts}
              pagination={state.context.pagination}
              onPageChange={(page) => {
                send({
                  type: "GO_TO_PAGE",
                  page,
                });
              }}
              onLoadMore={() => {
                send({
                  type: "LOAD_MORE_RESULTS",
                });
              }}
              onSort={(sorting) => {
                send({
                  type: "ALTER_FILTER",
                  newFilter: {
                    ...state.context.filter,
                    sort: sorting,
                  },
                } as AlterFilterEvent);
              }}
              activeSorting={
                state.context.filter.sort ?? selectDefaultSorting()
              }
              isLoading={!state.matches("productsDisplayed.idle")}
            />
          )}
        </div>
      </div>
      {!isMobile && (
        <div id="menu-content">
          <div id="category-sidebar">
            {retryCategoryLoad !== undefined && (
              <RetryButton
                onRetry={() => {
                  retryCategoryLoad();
                }}
              />
            )}
            {category !== null && (
              <CategorySidebar
                category={category}
                currentCategoryId={categoryId}
              />
            )}
            {filterSidebar}
          </div>
        </div>
      )}

      <div className="cleaner" />
    </div>
  );
};
